import type { WallSegment } from '@manudev/model';
import type { PropsElement } from '../props';
import type { BuildStore } from '../types';
import { outerFootprint } from './footprint';
import { boxesByElement, type Box } from './frame';
import { cutOpening } from './opening';
import {
  elevationOf,
  nextStoreyName,
  orderedStoreys,
  sourceStorey,
  storeyHeight,
  storeysInTheWay,
} from './storeys';
import type { BuildParams, BuildResult } from './types';

const SLAB_THICKNESS = 0.2;
const DEFAULT_WALL_THICKNESS = 0.2;
const MIN_LEAF = 0.05;
const EDGE = 0.1;

// Another floor on top of the highest inhabited one: its walls, one slab over their outline,
// and its doors, windows and rooms once more. What already stood at that level (a parapet, a
// roof terrace) is taken out first. Everything is read before the first edit, since an edit
// drops the mesh cache and the next read would re-export the whole model.
export async function addStorey(store: BuildStore, params: BuildParams): Promise<BuildResult> {
  const storeys = await orderedStoreys(store);
  const source = await sourceStorey(store, params.storey);
  const height = params.height ?? storeyHeight(storeys, source);
  const elevation = Number((elevationOf(source) + height).toFixed(6));
  const name = params.name ?? nextStoreyName(source.name);

  const segments = await store.wallSegments(source.expressID);
  if (!segments.length) throw new Error(`no wall axes on ${source.name}`);
  const footprint = outerFootprint(segments);
  const rooms = await store.spaceFootprints(source.expressID);

  const elements = await store.elements({ includeSpaces: true });
  const doors = openings(elements, source.expressID, 'IFCDOOR');
  const windows = openings(elements, source.expressID, 'IFCWINDOW');
  const boxes = boxesByElement(
    await store.mesh([...doors, ...windows].map((e) => e.expressID)),
    elevationOf(source),
  );

  const removed: number[] = [];
  for (const old of storeysInTheWay(storeys, elevation, height)) {
    for (const e of elements)
      if (e.storeyId === old.expressID) {
        await store.remove(e.expressID);
        removed.push(e.expressID);
      }
    await store.remove(old.expressID);
  }

  const created: number[] = [];
  created.push(await store.addStorey(name, elevation));
  const storeyId = created[0]!;
  const anchor = await store.anchor(storeyId);

  // parallel to segments: the new wall that stands where the copied one does, so an opening
  // copied off the old wall can be cut into the new one
  const walls: WallSegment[] = [];
  for (const [i, segment] of segments.entries()) {
    const wallId = await store.addWall(storeyId, {
      Start: [segment.start[0], segment.start[1], 0],
      End: [segment.end[0], segment.end[1], 0],
      Thickness: segment.thickness ?? DEFAULT_WALL_THICKNESS,
      Height: height,
      Name: `${name} wall ${i + 1}`,
    });
    created.push(wallId);
    walls.push({ ...segment, id: wallId });
  }

  created.push(
    await store.addSlab(storeyId, {
      Profile: 'polygon',
      OuterCurve: footprint,
      // the slab hangs under the storey datum so its top face is the new floor
      Position: [0, 0, -SLAB_THICKNESS],
      Thickness: SLAB_THICKNESS,
      Name: `${name} floor`,
    }),
  );

  for (const [kind, list] of [
    ['door', doors],
    ['window', windows],
  ] as const)
    for (const original of list) {
      const leaf = boxes.get(original.expressID);
      if (!leaf) continue;
      const seat = hostFor(walls, leaf);
      if (!seat) continue;
      const { fillId } = await cutOpening(store, anchor, {
        wall: seat.wall,
        offset: seat.offset,
        width: Math.min(seat.width, Math.max(span(seat.wall) - EDGE, MIN_LEAF)),
        height: Math.max(leaf.max[2] - leaf.min[2], MIN_LEAF),
        sill: Math.max(leaf.min[2], 0),
        kind,
        name: original.name || (kind === 'door' ? 'Door' : 'Window'),
      });
      created.push(fillId);
    }

  for (const [i, outline] of rooms.entries())
    created.push(
      await store.addSpace(storeyId, {
        Profile: 'polygon',
        OuterCurve: outline,
        Height: Math.max(height - SLAB_THICKNESS, 0.1),
        Name: `${name} room ${i + 1}`,
      }),
    );

  const cleared = removed.length ? `, ${removed.length} elements at that level taken out` : '';
  return {
    created,
    removed,
    note: `${name} at ${elevation} m from ${source.name}: ${segments.length} walls, 1 slab over ${footprint.length} points, ${doors.length} doors, ${windows.length} windows, ${rooms.length} rooms${cleared}`,
  };
}

function openings(elements: PropsElement[], storeyId: number, type: string): PropsElement[] {
  return elements.filter((e) => e.storeyId === storeyId && e.type.toUpperCase() === type);
}

interface Seat {
  wall: WallSegment;
  offset: number;
  width: number;
}

// Which new wall a copied door or window belongs in: the axis its middle sits closest to, and
// how far along that axis it lands. The original's wall-hosting is not in PropsElement, so
// the geometry has to say it.
function hostFor(walls: WallSegment[], leaf: Box): Seat | null {
  const middle: [number, number] = [
    (leaf.min[0] + leaf.max[0]) / 2,
    (leaf.min[1] + leaf.max[1]) / 2,
  ];
  let best: Seat | null = null;
  let nearest = Infinity;
  for (const wall of walls) {
    const length = span(wall);
    if (length < MIN_LEAF) continue;
    const dx = (wall.end[0] - wall.start[0]) / length;
    const dy = (wall.end[1] - wall.start[1]) / length;
    const t = (middle[0] - wall.start[0]) * dx + (middle[1] - wall.start[1]) * dy;
    const on = Math.min(Math.max(t, 0), length);
    const gap = Math.hypot(
      middle[0] - (wall.start[0] + dx * on),
      middle[1] - (wall.start[1] + dy * on),
    );
    if (gap >= nearest) continue;
    nearest = gap;
    // the leaf's extent along the wall, not its bounding box's longer side
    const width = Math.max(
      Math.abs((leaf.max[0] - leaf.min[0]) * dx) + Math.abs((leaf.max[1] - leaf.min[1]) * dy),
      MIN_LEAF,
    );
    best = {
      wall,
      offset: Math.min(Math.max(on, width / 2), Math.max(length - width / 2, width / 2)),
      width,
    };
  }
  return best;
}

const span = (wall: WallSegment) =>
  Math.hypot(wall.end[0] - wall.start[0], wall.end[1] - wall.start[1]);
